import React, { useContext, useState } from 'react';
import { T, useT, useTX } from '@transifex/react';
import { useQuery } from 'react-query';
import styled from 'styled-components';
import { Button } from 'src/antd';
import download from 'downloadjs';
import dayjs from 'dayjs';
import { SelectYearSimple } from 'src/ui/SelectMoment/SelectYearSimple';
import { SelectMonthSimple } from 'src/ui/SelectMoment/SelectMonthSimple';
import { Typography } from 'src/ui/Typography';
// eslint-disable-next-line import/no-cycle
import { useGetToken } from '../../../../../api/config';
import { PropertyContext } from '../../../../../context/property-context';
import { formatDate } from '../../../../../utils';
import { GetCalendar } from './api';
import GeneratingCalendar from './generating';
// eslint-disable-next-line import/no-cycle
import CompleteCalendar from './complete';
// eslint-disable-next-line import/no-cycle
import { useRouting } from '../../../../../routing/child-router-factory';

const ResponsiveContainer = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 0.5rem;
  column-gap: 0;
  margin-bottom: 3rem;

  @media screen and (min-width: 1200px) {
    flex-direction: row;
    column-gap: 2rem;
    row-gap: 0;
    margin-bottom: 0;
  }
`;

const SelectContainer = styled.div`
  min-width: 100%;

  @media screen and (min-width: 1200px) {
    min-width: 200px;
  }
`;

const SelectWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
  margin-bottom: 0;
  margin-top: 0.5rem;

  @media screen and (min-width: 1200px) {
    gap: 0;
    margin-bottom: 3rem;
  }
`;

const StyledSelectYear = styled(SelectYearSimple)`
  flex: 1;
  margin-right: 0.5rem;

  @media screen and (min-width: 1200px) {
    flex: none;
    margin-right: 1rem;
  }
`;

const StyledSelectMonth = styled(SelectMonthSimple)`
  flex: 1;
  width: 200px;

  @media screen and (min-width: 1200px) {
    flex: none;
  }
`;

const MainContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  height: 100%;
  width: 100%;
`;

const TitleText = styled.div`
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 4rem;
`;

const DownloadCalendar = () => {
  const token = useGetToken();
  const { id: propertyId, location } = useContext(PropertyContext);

  const [fromYear, setFromYear] = useState(dayjs().year());
  const [toYear, setToYear] = useState(dayjs().year());
  const [toMonth, setToMonth] = useState(dayjs().month());
  const [fromMonth, setFromMonth] = useState(dayjs().month());
  const [pdfData, setPdfData] = useState();

  const history = useRouting.useHistory();

  const t = useT();
  const tx = useTX();
  const cancelText = t('Cancel');
  const downloadText = t('Download');
  const collectionsString = t('Collections');

  const queryParameters = `api_key,${token}|from_date,${dayjs(
    `${fromYear}-${fromMonth + 1}-01`
  ).format('YYYY-MM-DD')}|to_date,${dayjs(`${toYear}-${toMonth + 1}-01`)
    .endOf('month')
    .format(
      'YYYY-MM-DD'
    )}|property_id,${propertyId}|language,${tx.getCurrentLocale()}`;

  const filename = `${collectionsString}_${location?.name}_${formatDate(
    dayjs(dayjs(`${fromYear}-01-01`).month(fromMonth))
  )}_${formatDate(dayjs())}.pdf`;

  const {
    data,
    refetch,
    isLoading: loading,
    isFetching: fetching,
  } = useQuery(
    ['get-collection-calendar', fromYear, fromMonth, token, queryParameters],
    () => GetCalendar(token, filename, `calendar`, queryParameters, false),
    {
      refetchOnWindowFocus: false,
      enabled: false,
      onSuccess: (response) => {
        setPdfData(response.data);
        const content = response.headers['content-type'];
        download(response.data, filename, content);
      },
    }
  );

  return (
    <MainContainer>
      {!loading && !fetching && !pdfData && (
        <>
          <TitleText>
            <T _str="Please select date" />
          </TitleText>
          <ResponsiveContainer>
            <SelectContainer>
              <Typography weight="semi-bold">
                <T _str="From" />
              </Typography>
              <SelectWrapper>
                <StyledSelectYear
                  value={fromYear}
                  onChange={(e) => setFromYear(e)}
                  validRange={[2018, 2030]}
                />
                <StyledSelectMonth
                  onChange={(e) => setFromMonth(e)}
                  value={fromMonth}
                />
              </SelectWrapper>
            </SelectContainer>
            <SelectContainer>
              <Typography weight="semi-bold">
                <T _str="To" />
              </Typography>
              <SelectWrapper>
                <StyledSelectYear
                  value={toYear}
                  onChange={(e) => setToYear(e)}
                  validRange={[2018, 2030]}
                />
                <StyledSelectMonth
                  onChange={(e) => setToMonth(e)}
                  value={toMonth}
                />
              </SelectWrapper>
            </SelectContainer>
          </ResponsiveContainer>
          <div style={{ display: 'flex', flexDirection: 'row' }}>
            <Button
              style={{ marginRight: '1rem' }}
              onClick={() => history.goBack()}
            >
              {cancelText}
            </Button>
            <Button type="primary" onClick={() => refetch()}>
              {downloadText}
            </Button>
          </div>
        </>
      )}
      {(loading || fetching) && <GeneratingCalendar />}
      {pdfData && (
        <CompleteCalendar
          data={data}
          setPdfData={setPdfData}
          filename={filename}
        />
      )}
    </MainContainer>
  );
};

export default DownloadCalendar;
